import { Link } from "@tanstack/react-router";
import { Wifi, WifiOff, RefreshCw } from "lucide-react";
import { useConnection } from "@/lib/offline/connection";
import { cn } from "@/lib/utils";

/**
 * Subtle pill showing connection state + pending offline-changes count.
 * Clicking opens the Sync Center.
 */
export function ConnectionBadge({ className }: { className?: string }) {
  const { state, pending } = useConnection();

  const dotClass =
    state === "online" ? "bg-emerald-500"
    : state === "syncing" ? "bg-amber-400"
    : "bg-rose-500";

  const label =
    state === "online" ? "Online"
    : state === "syncing" ? "Syncing"
    : "Offline";

  const Icon =
    state === "online" ? Wifi
    : state === "syncing" ? RefreshCw
    : WifiOff;

  return (
    <Link
      to="/settings/offline"
      title={`${label}${pending ? ` · ${pending} pending` : ""}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-foreground/80 backdrop-blur transition hover:bg-accent",
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", dotClass, state === "syncing" && "animate-pulse")} />
      <Icon className={cn("h-3 w-3", state === "syncing" && "animate-spin")} />
      <span className="hidden sm:inline">{label}</span>
      {pending > 0 && (
        <span className="ml-0.5 rounded-full bg-foreground/10 px-1.5 py-0.5 text-[10px] font-semibold">
          {pending}
        </span>
      )}
    </Link>
  );
}
